import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { authApi } from '../../api/client';
import { isAuthConfigured } from '../../lib/supabase';
import { Spinner } from '../common/Spinner';

/**
 * Wraps routes that require authentication.
 * - When auth is not configured (no Supabase env vars) → renders children directly
 * - Unauthenticated users → /login
 * - Authenticated users whose onboarding is 'unknown' → runs onboarding check
 * - Authenticated users with onboarding 'needed' → /onboarding
 * - Authenticated + onboarded → renders children
 */
export function ProtectedRoute() {
  const { session, onboardingStatus, setOnboardingStatus, setOrgContext } = useAuthStore();
  const location = useLocation();

  const isE2EBypass = import.meta.env.VITE_E2E_BYPASS === 'true';

  useEffect(() => {
    // Skip onboarding check when auth is not configured or in E2E mode
    if (!isAuthConfigured || isE2EBypass) return;
    if (!session || onboardingStatus !== 'unknown') return;

    // Run idempotent onboarding — persist org context on success.
    // Only treat a 403 (no org) as "onboarding needed"; any other error (429,
    // 500, network) leaves status 'unknown' so the next render retries.
    authApi
      .onboard()
      .then((result) => setOrgContext(result))
      .catch((err: unknown) => {
        const status =
          (err as { response?: { status?: number } })?.response?.status ??
          (err as { status?: number })?.status;
        if (status === 403) setOnboardingStatus('needed');
      });
  }, [session, onboardingStatus, setOnboardingStatus, setOrgContext]);

  // Bootstrap/dev/CI/E2E mode — render without auth
  if (!isAuthConfigured || isE2EBypass) {
    return <Outlet />;
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (onboardingStatus === 'unknown') {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-0">
        <Spinner size="lg" />
      </div>
    );
  }

  if (onboardingStatus === 'needed') {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}
